import { Loader2, RefreshCw, Wallet } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { useCustodialPanelState, useSnappyWalletState } from '@/features/custodial';

import { useAgentReactivation } from '../hooks/useAgentReactivation';

import ReactivationProgress from './ReactivationProgress';

/**
 * "Reactivate your Snappy Wallet" banner. Renders (real mode only) when the user
 * already HAS a wallet whose Hyperliquid agent needs a re-approval — either the
 * proactive status read reports it (so the fix appears BEFORE a failed trade), or
 * a trade just failed fast with `AGENT_REACTIVATION_REQUIRED`. The button fires
 * the one-time reactivate POST; it shows a pending state and the banner
 * self-clears once the status refetch reports the fresh approval. Reactivation
 * never auto-resubmits the trade — the user re-initiates it themselves.
 *
 * Gated on {@link useSnappyWalletState} rather than `needsReactivation` alone:
 * the backend also reports `needsReactivation` for users with no wallet at all,
 * where the reactivate POST throws `tradeSetupRequired` and the onboarding
 * prompt is the only correct instruction.
 */
const ReactivateAgentBanner: React.FC = () => {
    const snappy = useSnappyWalletState();
    const openDeposit = useCustodialPanelState((s) => s.openDeposit);
    const {
        isReactivating,
        reactivationStartedAtMs,
        reactivate,
        firstActivation,
        awaitingSignature,
    } = useAgentReactivation();

    // `isReactivating` keeps the banner mounted through the whole run. Gating on the status alone
    // unmounted it the moment the status refetch came back — and it comes back `needsReactivation:
    // false` almost immediately, because the backend read is db-only and the DB was already updated
    // when the job was enqueued. The user saw the banner vanish with no confirmation, and the
    // in-progress UI below (disabled button, progress bar) was never reachable.
    if (snappy.status !== 'reactivationRequired' && !isReactivating) return null;

    // reactivation runs the HyperCore bootstrap, which needs USDC in the custodial wallet. without
    // it the background job dies with HIP3_SETUP_FUNDING_REQUIRED minutes after the button says
    // "started", so ask for the deposit rather than offering an action that cannot succeed.
    // A run in progress outranks it: once the job exists, "deposit to activate" is stale advice
    // and would swallow the progress UI.
    if (
        !isReactivating &&
        snappy.status === 'reactivationRequired' &&
        snappy.fundingRequired
    ) {
        return (
            <div
                role="status"
                className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5"
            >
                <div className="flex items-start gap-2">
                    <Wallet size={14} className="mt-0.5 shrink-0 text-amber-400" />
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-amber-400">
                            Add funds to activate your Snappy Wallet
                        </p>
                        <p className="text-[11px] leading-relaxed text-muted-foreground">
                            Activation needs at least 2 USDC in your Snappy Wallet. Deposit to finish
                            setting it up, you only do this once.
                        </p>
                    </div>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDeposit()}
                    className="mt-2.5 w-full gap-1 border-amber-500/30 text-amber-400 hover:border-amber-500/50 hover:bg-amber-500/10"
                >
                    <Wallet size={14} />
                    Deposit to activate
                </Button>
            </div>
        );
    }

    // a never-activated wallet is being set up for the first time; everything else is a real
    // re-approval of an agent that expired or was pruned. A stale builder fee is no longer a
    // reason to be here at all: it is repaired silently, with no signature.
    const title = firstActivation
        ? 'Activate your Snappy Wallet'
        : 'Reactivate your Snappy Wallet';
    const action = firstActivation ? 'Activate' : 'Reactivate';
    let detail = firstActivation
        ? 'One signature finishes setting up your wallet for one-click trading.'
        : 'Re-approve your trading agent to keep placing trades.';
    if (isReactivating) {
        // no time promise: this waits for the JOB to finish, which includes on-chain steps. the button
        // stays disabled until it settles or fails, so the copy must not imply it is nearly done.
        detail =
            'Setting up your wallet. Keep this tab open, we will tell you as soon as it finishes.';
    } else if (awaitingSignature) {
        detail = `Setup is waiting for your signature. Press ${action} to confirm it in your wallet.`;
    }

    return (
        <div
            role="status"
            className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5"
        >
            <div className="flex items-start gap-2">
                <RefreshCw
                    size={14}
                    className={
                        isReactivating
                            ? 'mt-0.5 shrink-0 animate-spin text-amber-400'
                            : 'mt-0.5 shrink-0 text-amber-400'
                    }
                />
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-amber-400">
                        {title}
                    </p>
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                        {detail}
                    </p>
                </div>
            </div>
            <Button
                variant="outline"
                size="sm"
                onClick={reactivate}
                disabled={isReactivating}
                className="mt-2.5 w-full gap-1 border-amber-500/30 text-amber-400 hover:border-amber-500/50 hover:bg-amber-500/10"
            >
                {isReactivating ? (
                    <>
                        <Loader2 size={14} className="animate-spin" />
                        {firstActivation ? 'Activating…' : 'Reactivating…'}
                    </>
                ) : (
                    <>
                        <RefreshCw size={14} />
                        {action}
                    </>
                )}
            </Button>
            {isReactivating && (
                <ReactivationProgress startedAtMs={reactivationStartedAtMs} />
            )}
        </div>
    );
};

ReactivateAgentBanner.displayName = 'ReactivateAgentBanner';

export default ReactivateAgentBanner;
